import { Component, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { DashboardWidget } from 'app/models/entities/dashboard-widget';
import { OrganizationService } from 'app/services/organization/organization.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Router } from '@angular/router';
import { DialogDashWidgetSortComponent } from './dialog-dash-widget-sort/dialog-dash-widget-sort.component';
import { MatDialogSizes } from '../../../../../enums/mat-dialog-sizes.enum';
import { GrowlerService } from '../../../../../services/growler.service';

@UntilDestroy()
@Component({
    selector: 'app-dash-widgets',
    templateUrl: './dash-widgets.component.html',
    styleUrls: ['./dash-widgets.component.scss'],
})
export class DashWidgetsComponent implements OnInit
{
    /** Array of objects representing the modules that can be shown/hidden for dash widgets. */
    allWidgets: DashboardWidget[] = [];
    /** The widgets that are actually enabled */
    enabledWidgets: DashboardWidget[] = [];
    /** Array of the widgets that are actually visible on screen in custom 'slider' */
    visibleWidgets: DashboardWidget[] = [];
    /** Extremely simple slider handling */
    whichSet: 'first' | 'middle' | 'last';
    currentIndex = 0;

    constructor(
            private _dialog: MatDialog,
            private _organizationService: OrganizationService,
            private _router: Router,
            private _growler: GrowlerService,
    ) {
    }

    ngOnInit() {
        this.getDashboardWidgets();
    }

    getDashboardWidgets() {
        this._organizationService.getOrgDashboardWidgets().pipe(untilDestroyed(this)).subscribe({
            next: (widgets: DashboardWidget[]) => {
                this.allWidgets = widgets;
                this.enabledWidgets = this.allWidgets.filter(w => w.isEnabled);
                this.incrementWidgets('start');
            },
            error: () => this._growler.error('Error', 'There was an error loading the widgets for your dashboard.'),
        });
    }

    incrementWidgets(current: 'start' | 'first' | 'middle' | 'last') {
        const size = 4;

        switch (current) {
            case 'last':
                // should never get here, but just in case...
                return;
            case 'start':
                this.currentIndex = 0;
                this.whichSet = 'first';
                this.visibleWidgets = this.enabledWidgets.slice(this.currentIndex, size);
                break;
            case  'first':
                this.currentIndex = size;
                this.whichSet = 'middle';
                this.visibleWidgets = this.enabledWidgets.slice(this.currentIndex, (this.currentIndex + size));
                break;
            case 'middle':
                this.currentIndex += size;
                if ((this.currentIndex + size) >= this.enabledWidgets.length) {
                    this.whichSet = 'last';
                }
                this.visibleWidgets = this.enabledWidgets.slice(this.currentIndex, (this.currentIndex + size));
                break;
        }
    }

    decrementWidgets(current: 'start' | 'first' | 'middle' | 'last') {
        const size = 4;

        switch (current) {
            case 'first':
            case 'start':
                // should never get here, but just in case...
                return;
            case 'middle':
                this.currentIndex -= size;
                if (this.currentIndex <= 0) {
                    this.whichSet = 'first';
                }
                this.visibleWidgets = this.enabledWidgets.slice(this.currentIndex, (this.currentIndex + size));
                break;
            case 'last':
                this.currentIndex -= size;
                this.whichSet = 'middle';
                this.visibleWidgets = this.enabledWidgets.slice(this.currentIndex, (this.currentIndex + size));
                break;
        }
    }

    /**
     * Open the dialog for sorting widgets
     */
    changeSort() {
        const dialogRef = this._dialog.open(DialogDashWidgetSortComponent, {
            width: MatDialogSizes.SM,
            data: this.allWidgets, // send in the full set (some might be disabled)
        });

        dialogRef.afterClosed().subscribe((newWidgets: DashboardWidget[]) => {
            // if close with 'update' = true, update the widget display
            if (newWidgets.length) {
                this.allWidgets = newWidgets;
                this.enabledWidgets = this.allWidgets.filter(w => w.isEnabled);
                this.incrementWidgets('start');
            }
        });

    }

    /**
     * Navigate from widget click; some special cases were needed do to uri parsing
     * @param module
     */
    doNavigation(module: DashboardWidget) {
        if (module.image.includes('undeliverable')) {
            this._router.navigate(
                    ['/app/communications/org-log'],
                    {queryParams: {status: 'undeliverable'}},
            );
        } else if (module.image.includes('unconfirmed')) {
            this._router.navigate([
                '/app/people',
                {tab: 'unconfirmed'},
            ]);
        } else {
            this._router.navigate([module.url]);
        }

    }
}
